// src/App.jsx
"use client";

import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";

// UI / Toast
import { Toaster } from "./components/ui/toaster";

// Auth wiring
import { AuthProvider } from "./components/context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";

// Pages
import Auth from "./components/Auth";
import NewHomePage from "./components/NewHomePage";
import ClientDashboard from "./components/ClientDashboard";
import ReschedulePage from "./components/ReschedulePage";
import BookingFlowWrapper from "./components/BookingFlowWrapper";
import MatchesModern from "./components/MatchesModern";
import Confirm from "./components/Confirm";
import Payment from "./components/Payment";
import AdminChargeNoShow from "./components/AdminChargeNoShow";

// Gift Card Pages
import GiftCardPurchase from "./components/GiftCardPurchase";
import GiftCardCustomize from "./components/GiftCardCustomize";
import GiftCardPayment from "./components/GiftCardPayment";
import GiftCardSuccess from "./components/GiftCardSuccess";
import GiftCardRedeem from "./components/GiftCardRedeem";
import MassageClinicSearch from "./components/MassageClinicSearch";
import ChristmasGiftPromo from "./components/ChristmasGiftPromo";
import ChristmasGiftPromoWithPopup from "./components/ChristmasGiftPromoWithPopup";
import HolidayGiftCardThankYou from "./components/HolidayGiftCardThankYou";

// Rewards & Referrals
import LoyaltyDashboard from "./components/LoyaltyDashboard";
import ReferralPage from "./components/ReferralPage";

// Contact Us
import ContactUs from "./components/ContactUs";

// Services Page
import Services from "./components/Services";

// About Us & Blogs
import AboutUs from "./components/AboutUs";
import BlogList from "./components/BlogList";
import BlogDetail from "./components/BlogDetail";

// Blog Admin (OTP-gated, noindex)
import BlogAdminLogin from "./components/blogadmin/BlogAdminLogin";
import BlogAdminDashboard from "./components/blogadmin/BlogAdminDashboard";
import BlogEditor from "./components/blogadmin/BlogEditor";
import BlogAdminProtectedRoute from "./components/blogadmin/BlogAdminProtectedRoute";
import CityPage from "./components/CityPage";
import ClinicPage from "./components/ClinicPage";
import CityMassageTherapyPage from "./components/CityMassageTherapyPage";
import TherapistPage from "./components/TherapistPageEnhanced";
import TherapistsDirectory from "./components/TherapistsDirectory";

// Legal Pages
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsOfService from "./components/TermsOfService";
import CancellationPolicy from "./components/CancellationPolicy";
import ManageCookies from "./components/ManageCookies";
import NotFound from "./components/NotFound";
// Chatbot Training
import ChatBotTraining from "./components/ChatBotTraining";

// Post-Appointment Feedback
import PostAppointmentFeedback from "./components/PostAppointmentFeedback";

// Cookie Consent
import CookieConsentManager from "./components/CookieConsentManager";

// Centerfy Tracking
import CenterfyTracking from "./components/CenterfyTracking";

// Auto Tracking Provider
import AutoTrackingProvider from "./components/AutoTrackingProvider";

// Mobile bottom tab bar (app-like UX on mobile)
import MobileBottomNav from "./components/MobileBottomNav";

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AutoTrackingProvider>
            <CenterfyTracking>
              <Routes>
                <Route path="/" element={<NewHomePage />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/dashboard" element={<ClientDashboard />} />
                <Route path="/reschedule" element={<ReschedulePage />} />
                <Route
                  path="/reschedule/:appointmentId"
                  element={<ReschedulePage />}
                />

                <Route path="/book" element={<BookingFlowWrapper />} />

                <Route path="/book/matches" element={<MatchesModern />} />
                <Route path="/book/confirm" element={<Confirm />} />
                <Route path="/book/payment" element={<Payment />} />
                <Route
                  path="/admin/charge-no-show"
                  element={<AdminChargeNoShow />}
                />

                {/* Gift Card Routes */}
                <Route path="/gift-cards" element={<GiftCardPurchase />} />
                <Route
                  path="/gift-cards/christmas"
                  element={<ChristmasGiftPromoWithPopup />}
                />
                <Route
                  path="/gift-cards/holiday/thank-you"
                  element={<HolidayGiftCardThankYou />}
                />
                <Route
                  path="/gift-cards/customize"
                  element={<GiftCardCustomize />}
                />
                <Route
                  path="/gift-cards/payment"
                  element={<GiftCardPayment />}
                />
                <Route
                  path="/gift-cards/success"
                  element={<GiftCardSuccess />}
                />
                <Route path="/gift-cards/redeem" element={<GiftCardRedeem />} />
                <Route
                  path="/gift-cards/redeem/:code"
                  element={<GiftCardRedeem />}
                />
                <Route
                  path="/gift-cards/clinics"
                  element={<MassageClinicSearch />}
                />

                {/* Rewards & Referrals Routes - Unified System */}
                {/* Note: LoyaltyDashboard code exists but is not used per requirements */}
                <Route path="/rewards" element={<ReferralPage />} />
                <Route path="/referrals" element={<ReferralPage />} />

                {/* Contact Us Route */}
                <Route path="/contact" element={<ContactUs />} />

                {/* Services Route */}
                <Route path="/services" element={<Services />} />

                {/* About Us & Blogs Routes */}
                <Route path="/about" element={<AboutUs />} />
                <Route path="/blog" element={<BlogList />} />
                <Route path="/blog/:id" element={<BlogDetail />} />
                {/* Legacy routes for backward compatibility */}
                <Route path="/blogs" element={<BlogList />} />
                <Route path="/blogs/:blogdid" element={<BlogDetail />} />

                {/* Blog Admin (OTP-gated, noindex) */}
                <Route path="/blog-admin/login" element={<BlogAdminLogin />} />
                <Route
                  path="/blog-admin"
                  element={
                    <BlogAdminProtectedRoute>
                      <BlogAdminDashboard />
                    </BlogAdminProtectedRoute>
                  }
                />
                <Route
                  path="/blog-admin/new"
                  element={
                    <BlogAdminProtectedRoute>
                      <BlogEditor />
                    </BlogAdminProtectedRoute>
                  }
                />
                <Route
                  path="/blog-admin/edit/:id"
                  element={
                    <BlogAdminProtectedRoute>
                      <BlogEditor />
                    </BlogAdminProtectedRoute>
                  }
                />
                {/* Static Blog - Dedicated Page */}
                <Route
                  path="/how-to-find-a-good-massage-therapist-home-visits"
                  element={<BlogDetail />}
                />

                {/* City/Location Pages for Local SEO */}
                <Route path="/locations/:city" element={<CityPage />} />
                <Route
                  path="/:citySlug/massage-therapy"
                  element={<CityMassageTherapyPage />}
                />

                {/* Clinic Detail Pages */}
                <Route path="/clinics/:clinicSlug" element={<ClinicPage />} />

                {/* Therapist Directory - Browse All Therapists */}
                <Route path="/therapists" element={<TherapistsDirectory />} />
                <Route
                  path="/find-therapist"
                  element={<TherapistsDirectory />}
                />
                <Route
                  path="/massage-therapists"
                  element={<TherapistsDirectory />}
                />

                {/* Therapist Profile Pages - SEO Optimized with City */}
                <Route
                  path="/therapist/:city/:therapistName"
                  element={<TherapistPage />}
                />
                {/* Legacy routes for backward compatibility */}
                <Route
                  path="/massage-therapists/:therapistSlug"
                  element={<TherapistPage />}
                />
                <Route
                  path="/therapists/:therapistSlug"
                  element={<TherapistPage />}
                />

                {/* Chatbot Training Route */}
                <Route path="/chatbot-training" element={<ChatBotTraining />} />

                {/* Post-Appointment Feedback Route */}
                <Route
                  path="/post-appointment"
                  element={<PostAppointmentFeedback />}
                />

                {/* Legal Pages Routes */}
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route
                  path="/cancellation-policy"
                  element={<CancellationPolicy />}
                />
                <Route path="/manage-cookies" element={<ManageCookies />} />

                <Route path="*" element={<NotFound />} />
              </Routes>
              <MobileBottomNav />
              <Toaster />
              <CookieConsentManager />
            </CenterfyTracking>
          </AutoTrackingProvider>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
